import { useRef, useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  STAGE, ZONES, NODE_POS, TUBES, TUBE_STYLE,
  Node, Label, Pill,
  O2CylinderArt, FlowmeterArt, VaporiserArt, SodaLimeArt, PopOffArt,
  ReservoirBagArt, ScavengingArt, ClipboardArt, manoPoint, manoArc,
} from "@/components/ippv/IppvArt";

const PHASE_LABEL = { setup: "Initial setup", leak: "Leak test", ippv: "Deliver IPPV", done: "Complete" };

const LEGEND = [
  { c: "#38bdf8", t: "Fresh gas" },
  { c: "#2563eb", t: "Inspired" },
  { c: "#14b8a6", t: "Expired" },
  { c: "#a78bfa", t: "Scavenged" },
];

// scale the fixed design-space stage to the available container width
function useFitScale() {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      setScale(Math.min(1, w / STAGE.w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, scale];
}

export function IppvCircuit({
  phase, next = {}, onFixLeak,
  // state
  gasFlowing, o2On, spareChecked, flow, vapFilled, vaporiserPct, vaporiserOff,
  vaporiserBackOn, sodaLimeChecked, circuitChecked, scavengingOn, preuseSigned, setupCoreReady,
  aplClosed, occluded, leaking, popClosed, breathsDone, breathCount,
  displayPressure, nx, ny, lungsInflated, bagOverfilled,
  // handlers
  onO2, onSpare, onFlow, onVaporiser, onSodaLime, onBag, onPopoff, onPatient, onScavenging, onClipboard,
}) {
  next = next || {};
  const [wrapRef, scale] = useFitScale();
  const isSetup = phase === "setup", isLeak = phase === "leak", isIppv = phase === "ippv";
  const vapText = vaporiserBackOn ? "ON 2%" : vaporiserOff ? "OFF ✓" : vaporiserPct > 0 ? "ON 2%" : vapFilled ? "FILLED" : "CHECK";

  // per-node interaction state
  const N = {
    spare:     { on: onSpare,     done: spareChecked,           enabled: isSetup },
    o2:        { on: onO2,        done: o2On,                   enabled: isSetup },
    flowmeter: { on: onFlow,      done: flow > 0,               enabled: isSetup },
    vaporiser: { on: onVaporiser, done: isIppv ? vaporiserBackOn : vaporiserPct > 0, enabled: isSetup || isIppv },
    clipboard: { on: onClipboard, done: preuseSigned,           enabled: isSetup && setupCoreReady },
    sodalime:  { on: onSodaLime,  done: sodaLimeChecked,        enabled: isSetup },
    scavenging:{ on: onScavenging,done: scavengingOn,           enabled: isSetup },
    bag:       { on: onBag,       done: isSetup ? circuitChecked : false, enabled: isSetup || isLeak || isIppv, danger: bagOverfilled },
    popoff:    { on: onPopoff,    done: isLeak ? aplClosed : false, enabled: isLeak || isIppv, danger: isIppv && popClosed },
    patient:   { on: onPatient,   done: isLeak ? occluded : false, enabled: isLeak },
  };
  const hl = (id) => next.node === id && N[id]?.enabled && !N[id]?.done;

  const readout = [
    { k: "Pressure", v: `${displayPressure} cmH₂O` },
    { k: "O₂ flow", v: `${flow} L/min` },
    { k: "Vaporiser", v: vapText },
    { k: "Breaths", v: `${breathCount}/3` },
  ];

  return (
    <div>
      <Card className="border-slate-200 overflow-hidden shadow-sm">
        <CardContent className="p-0">
          {/* readout strip */}
          <div className="flex items-stretch border-b border-slate-100 bg-slate-900 text-white" data-testid="ippv-readout">
            <div className="px-4 py-2.5 flex items-center">
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sky-300" data-testid="circuit-phase-label">
                {PHASE_LABEL[phase] || "Circuit"}
              </span>
            </div>
            <div className="ml-auto flex divide-x divide-white/10">
              {readout.map((r) => (
                <div key={r.k} className="px-4 py-1.5 text-right min-w-[92px]">
                  <div className="text-[9px] uppercase tracking-wider text-slate-400">{r.k}</div>
                  <div className="text-[13px] font-mono font-semibold" data-testid={`readout-${r.k.toLowerCase().split(" ")[0]}`}>{r.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* scalable stage */}
          <div ref={wrapRef} className="w-full bg-[#fbfdff]">
            <div style={{ width: STAGE.w * scale, height: STAGE.h * scale, margin: "0 auto" }}>
              <div className="relative" style={{ width: STAGE.w, height: STAGE.h, transform: `scale(${scale})`, transformOrigin: "top left" }}>
                {/* background: grid, zones, tubing */}
                <svg width={STAGE.w} height={STAGE.h} className="absolute inset-0" style={{ pointerEvents: "none" }}>
                  <defs>
                    <pattern id="grid" width="26" height="26" patternUnits="userSpaceOnUse">
                      <path d="M26 0 L0 0 0 26" fill="none" stroke="#e8eef5" strokeWidth="1" />
                    </pattern>
                    {["fresh", "insp", "exp", "vent"].map((k) => (
                      <marker key={k} id={`arrow-${k}`} markerWidth="9" markerHeight="9" refX="6" refY="3" orient="auto" markerUnits="userSpaceOnUse">
                        <path d="M0 0 L7 3 L0 6 Z" fill={TUBE_STYLE[k].color} />
                      </marker>
                    ))}
                  </defs>
                  <rect x="0" y="0" width={STAGE.w} height={STAGE.h} fill="url(#grid)" />
                  {/* zones */}
                  {ZONES.map((z) => (
                    <g key={z.label}>
                      <rect x={z.x} y={z.y} width={z.w} height={z.h} rx="18" fill="#ffffff" opacity="0.55" stroke="#dbe3ec" strokeWidth="1.5" strokeDasharray="2 6" />
                      <text x={z.x + 16} y={z.y + 22} fontSize="11" fontWeight="700" letterSpacing="1.5" className="fill-slate-400">{z.label.toUpperCase()}</text>
                    </g>
                  ))}
                  {/* tubing base */}
                  {TUBES.map((t, i) => (
                    <path key={`b${i}`} d={t.d} fill="none" stroke="#cbd5e1" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
                  ))}
                  {/* tubing coloured + arrows */}
                  {TUBES.map((t, i) => {
                    const s = TUBE_STYLE[t.kind];
                    if (t.kind === "base") return null;
                    return (
                      <path key={`c${i}`} d={t.d} fill="none" stroke={s.color} strokeWidth={s.w} strokeLinecap="round"
                        markerEnd={s.arrow ? `url(#arrow-${s.arrow})` : undefined} />
                    );
                  })}
                  {/* animated gas flow when flowing */}
                  {gasFlowing && TUBES.filter((t) => TUBE_STYLE[t.kind].flow).map((t, i) => (
                    <path key={`g${i}`} d={t.d} fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" className="gas-flow" opacity="0.85" />
                  ))}
                </svg>

                {/* ── machine zone ── */}
                <Node testid="ippv-spare-tank" onClick={N.spare.on} pos={NODE_POS.spare} done={N.spare.done} disabled={!N.spare.enabled} highlight={hl("spare")} hint={next.hint}>
                  <O2CylinderArt on={spareChecked} w={44} />
                  <Label>Spare O₂</Label>
                  <Pill ok={spareChecked} text={spareChecked ? "FULL ✓" : "CHECK"} />
                </Node>

                <Node testid="ippv-o2-tank" onClick={N.o2.on} pos={NODE_POS.o2} done={N.o2.done} disabled={!N.o2.enabled} highlight={hl("o2")} hint={next.hint}>
                  <O2CylinderArt on={o2On} w={54} />
                  <Label>O₂ tank</Label>
                  <Pill ok={o2On} text={o2On ? "OPEN" : "OFF"} />
                </Node>

                <Node testid="ippv-flowmeter" onClick={N.flowmeter.on} pos={NODE_POS.flowmeter} done={N.flowmeter.done} disabled={!N.flowmeter.enabled} highlight={hl("flowmeter")} hint={next.hint}>
                  <FlowmeterArt flow={flow} />
                  <Label>Flowmeter</Label>
                  <Pill ok={flow > 0} text={`${flow} L/min`} />
                </Node>

                <Node testid="ippv-vaporiser" onClick={N.vaporiser.on} pos={NODE_POS.vaporiser} done={N.vaporiser.done} disabled={!N.vaporiser.enabled} highlight={hl("vaporiser")} hint={next.hint}>
                  <VaporiserArt pct={vaporiserPct} off={vaporiserOff} backOn={vaporiserBackOn} />
                  <Label>Vaporiser</Label>
                  <Pill ok={vaporiserBackOn || vaporiserOff || vaporiserPct > 0} text={vapText} />
                </Node>

                <Node testid="ippv-preuse" onClick={N.clipboard.on} pos={NODE_POS.clipboard} done={N.clipboard.done} disabled={!N.clipboard.enabled} highlight={hl("clipboard")} hint={next.hint}>
                  <ClipboardArt signed={preuseSigned} />
                  <Label>Pre-use check</Label>
                  <Pill ok={preuseSigned} text={preuseSigned ? "SIGNED ✓" : "SIGN"} />
                </Node>

                {/* ── breathing circle zone ── */}
                <Node testid="ippv-popoff" onClick={N.popoff.on} pos={NODE_POS.popoff} done={N.popoff.done} danger={N.popoff.danger} disabled={!N.popoff.enabled} highlight={hl("popoff")} hint={next.hint}>
                  <PopOffArt closed={popClosed} />
                  <Label>Pop-off (APL)</Label>
                  <Pill ok={!popClosed} danger={popClosed} text={popClosed ? "CLOSED" : "OPEN"} />
                </Node>

                <Node testid="ippv-scavenging" onClick={N.scavenging.on} pos={NODE_POS.scavenging} done={N.scavenging.done} disabled={!N.scavenging.enabled} highlight={hl("scavenging")} hint={next.hint}>
                  <ScavengingArt on={scavengingOn} />
                  <Label>Scavenging</Label>
                  <Pill ok={scavengingOn} text={scavengingOn ? "ON ✓" : "OFF"} />
                </Node>

                <Node testid="ippv-bag" onClick={N.bag.on} pos={NODE_POS.bag} done={N.bag.done} danger={N.bag.danger} disabled={!N.bag.enabled} highlight={hl("bag")} hint={next.hint}>
                  {bagOverfilled && (
                    <span className="absolute -top-2 -right-2 z-10 text-[8px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full animate-pulse" data-testid="bag-overfill-warning">OVER-INFLATING</span>
                  )}
                  <ReservoirBagArt filled={gasFlowing} squeezed={lungsInflated} overfilled={bagOverfilled} />
                  <Label>Reservoir bag</Label>
                  <Pill ok={!bagOverfilled} danger={bagOverfilled} text={isSetup ? (circuitChecked ? "CONNECTED ✓" : "CONNECT") : "SQUEEZE"} />
                </Node>

                <Node testid="ippv-sodalime" onClick={N.sodalime.on} pos={NODE_POS.sodalime} done={N.sodalime.done} disabled={!N.sodalime.enabled} highlight={hl("sodalime")} hint={next.hint}>
                  <SodaLimeArt checked={sodaLimeChecked} exhausted={false} />
                  <Label>Soda lime</Label>
                  <Pill ok={sodaLimeChecked} text={sodaLimeChecked ? "FRESH ✓" : "CHECK"} />
                </Node>

                {/* Manometer (display) */}
                <div className="absolute" style={{ left: NODE_POS.manometer.left, top: NODE_POS.manometer.top, width: 120, height: 120 }} data-testid="ippv-manometer">
                  <svg width="120" height="120">
                    <circle cx="60" cy="60" r="55" fill="#1e293b" />
                    <circle cx="60" cy="60" r="50" fill="#fff" stroke="#cbd5e1" strokeWidth="1.5" />
                    <path d={manoArc(0, 20, 42)} fill="none" stroke="#22c55e" strokeWidth="6" strokeLinecap="round" />
                    <path d={manoArc(20, 30, 42)} fill="none" stroke="#f59e0b" strokeWidth="6" />
                    <path d={manoArc(30, 60, 42)} fill="none" stroke="#ef4444" strokeWidth="6" strokeLinecap="round" />
                    {[0, 10, 20, 30, 40, 50, 60].map((v) => {
                      const [x1, y1] = manoPoint(v, 34), [x2, y2] = manoPoint(v, 30), [tx, ty] = manoPoint(v, 22);
                      return (
                        <g key={v}>
                          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#475569" strokeWidth="1.6" />
                          <text x={tx} y={ty + 3} textAnchor="middle" fontSize="8" className="fill-slate-500">{v}</text>
                        </g>
                      );
                    })}
                    <line x1="60" y1="60" x2={nx} y2={ny} stroke={displayPressure > 25 ? "#ef4444" : "#0047AB"} strokeWidth="3" strokeLinecap="round" style={{ transition: "all 0.4s ease" }} />
                    <circle cx="60" cy="60" r="5" fill="#1e293b" />
                    <text x="60" y="86" textAnchor="middle" className="fill-slate-500" fontSize="9">cmH₂O</text>
                  </svg>
                  <div className="text-center text-[11px] font-semibold text-slate-600 -mt-1">Manometer</div>
                </div>

                {/* Patient (clickable to occlude during leak test) */}
                <Node testid="ippv-patient" onClick={N.patient.on} pos={NODE_POS.patient} done={N.patient.done} disabled={!N.patient.enabled} highlight={hl("patient")} hint={next.hint}>
                  {isLeak && occluded && (
                    <span className="absolute -top-2 right-3 z-10 text-[8px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-full" data-testid="patient-occluded-badge">OCCLUDED</span>
                  )}
                  <svg width="150" height="140" viewBox="0 0 150 150">
                    <rect x="70" y="2" width="10" height="30" rx="3" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="1" />
                    {isLeak && occluded && <circle cx="75" cy="6" r="7" fill="#f59e0b" stroke="#b45309" strokeWidth="1.5" />}
                    <g style={{ transformOrigin: "75px 95px", transition: "transform 0.4s ease" }} transform={lungsInflated ? "scale(1.12)" : "scale(1)"}>
                      <rect x="71" y="30" width="8" height="26" rx="2" fill="#9ca3af" />
                      <path d="M75 54 L58 66 M75 54 L92 66" stroke="#9ca3af" strokeWidth="4" strokeLinecap="round" fill="none" />
                      <path d="M70 56 C60 60 44 74 42 100 C41 118 54 128 64 122 C72 117 73 96 72 78 Z" fill={lungsInflated ? "#fca5a5" : "#fecdd3"} stroke="#fb7185" strokeWidth="2" />
                      <path d="M80 56 C90 60 106 74 108 100 C109 118 96 128 86 122 C78 117 77 96 78 78 Z" fill={lungsInflated ? "#fca5a5" : "#fecdd3"} stroke="#fb7185" strokeWidth="2" />
                    </g>
                  </svg>
                  <Label>Patient (ET tube)</Label>
                  {isLeak && <Pill ok={occluded} text={occluded ? "OCCLUDED" : "OCCLUDE"} />}
                </Node>

                {/* circuit / leak indicator (clickable to fix during leak test) */}
                <button
                  type="button"
                  onClick={leaking ? onFixLeak : undefined}
                  disabled={!leaking}
                  data-testid="ippv-circuit"
                  className={`absolute text-[10px] font-semibold rounded-full px-2.5 py-1 border-2 transition-colors ${
                    leaking
                      ? "bg-rose-50 border-rose-400 text-rose-700 ring-2 ring-rose-200 animate-pulse cursor-pointer"
                      : circuitChecked
                      ? "bg-emerald-50 border-emerald-400 text-emerald-700 cursor-default"
                      : "bg-white border-slate-300 text-slate-500 cursor-default"
                  }`}
                  style={{ left: 470, top: 436 }}
                >
                  {leaking ? "⚠ Leak — click to fix" : circuitChecked ? "Circuit ✓" : "Circuit"}
                </button>
              </div>
            </div>
          </div>

          {/* legend */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 border-t border-slate-100 bg-white">
            {LEGEND.map((l) => (
              <span key={l.t} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                <span className="w-4 h-[3px] rounded-full" style={{ background: l.c }} /> {l.t}
              </span>
            ))}
            <span className="ml-auto text-[10px] text-slate-400">Click a component to operate it</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
